import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, ShieldCheck, Warning, Microphone, Plus, Trash } from "@phosphor-icons/react";

const STEPS = ["about","verify","studio","portfolio","services","availability","preferences","payout","preview","launch"];
const LABELS = { about:"About You", verify:"Verify Yourself", studio:"Build Your Studio", portfolio:"Show Your Work", services:"Services", availability:"Availability", preferences:"Booking Preferences", payout:"Get Paid", preview:"Studio Preview", launch:"Launch" };

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("about");
  const [data, setData] = useState({});
  const [percent, setPercent] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/onboarding").then((r) => {
      setStep(r.data.step && STEPS.includes(r.data.step) ? r.data.step : "about");
      setData(r.data.data || {});
      setPercent(r.data.percent || 0);
    }).catch(() => {});
  }, [user]);

  const save = async (nextStep, patch = {}) => {
    setBusy(true);
    try {
      const merged = { ...data, ...patch };
      setData(merged);
      const r = await api.post("/onboarding", { step: nextStep, data: patch });
      setPercent(r.data.percent);
      setStep(nextStep);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const goBack = () => {
    const i = STEPS.indexOf(step);
    if (i > 0) setStep(STEPS[i - 1]);
  };

  if (!user) return <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="ob-signin">Sign in to onboard.</div>;

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-32" data-testid="onboarding-page">
      <div className="flex items-center justify-between mb-4">
        <button onClick={goBack} className="text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-gold flex items-center gap-1" data-testid="ob-back">
          <ArrowLeft size={12} /> Back
        </button>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60" data-testid="ob-progress">Studio setup — {percent}% complete</p>
      </div>
      <div className="w-full h-1 bg-cream/10 mb-8">
        <div className="h-1 bg-gold transition-all" style={{ width: `${percent}%` }} />
      </div>
      <p className="overline mb-2">{LABELS[step]}</p>

      {step === "about" && <StepAbout data={data} onNext={(p) => save("verify", p)} />}
      {step === "verify" && <StepVerify data={data} onNext={(p) => save("studio", p)} />}
      {step === "studio" && <StepStudio data={data} onNext={(p) => save("portfolio", p)} />}
      {step === "portfolio" && <StepPortfolio data={data} onNext={(p) => save("services", p)} />}
      {step === "services" && <StepServices data={data} onNext={(p) => save("availability", p)} />}
      {step === "availability" && <StepAvailability data={data} onNext={(p) => save("preferences", p)} />}
      {step === "preferences" && <StepPreferences data={data} onNext={(p) => save("payout", p)} />}
      {step === "payout" && <StepPayout data={data} onNext={(p) => save("preview", p)} />}
      {step === "preview" && <StepPreview data={data} onNext={(p) => save("launch", p)} />}
      {step === "launch" && <StepLaunch data={data} onLaunched={async () => { await refresh(); nav("/my-studio"); }} />}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-cream/60">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Next({ onClick, testid = "ob-next", label = "Continue", disabled }) {
  return (
    <div className="mt-8">
      <button data-testid={testid} disabled={disabled} onClick={onClick} className="btn-gold px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2 disabled:opacity-40">
        {label} <ArrowRight size={12} weight="bold" />
      </button>
    </div>
  );
}

function StepAbout({ data, onNext }) {
  const [f, set] = useState({
    legal_name: data.legal_name || "",
    public_name: data.public_name || "",
    dob: data.dob || "",
    email: data.email || "",
    mobile: data.mobile || "",
    location: data.location || "",
    years: data.years || "",
    specialties: data.specialties || [],
    bio: data.bio || "",
  });
  const ok = f.legal_name && f.public_name && f.email;
  const specs = ["short","fade","beard","curly","long","braids","colour"];
  return (
    <div className="space-y-5" data-testid="ob-step-about">
      <h1 className="font-display text-3xl">Tell us about yourself.</h1>
      <Row label="Legal name"><input data-testid="in-legal-name" className="input-line" value={f.legal_name} onChange={(e) => set({ ...f, legal_name: e.target.value })} /></Row>
      <Row label="Public display name"><input data-testid="in-public-name" className="input-line" value={f.public_name} onChange={(e) => set({ ...f, public_name: e.target.value })} /></Row>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Date of birth"><input type="date" data-testid="in-dob" className="input-line" value={f.dob} onChange={(e) => set({ ...f, dob: e.target.value })} /></Row>
        <Row label="Years of experience"><input type="number" min="0" data-testid="in-years" className="input-line" value={f.years} onChange={(e) => set({ ...f, years: e.target.value })} /></Row>
      </div>
      <Row label="Email"><input type="email" data-testid="in-email" className="input-line" value={f.email} onChange={(e) => set({ ...f, email: e.target.value })} /></Row>
      <Row label="Mobile"><input type="tel" data-testid="in-mobile" className="input-line" value={f.mobile} onChange={(e) => set({ ...f, mobile: e.target.value })} /></Row>
      <Row label="General location (suburb)"><input data-testid="in-location" className="input-line" value={f.location} onChange={(e) => set({ ...f, location: e.target.value })} /></Row>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60 mb-2">Specialties</p>
        <div className="flex flex-wrap gap-2">
          {specs.map((s) => {
            const on = f.specialties.includes(s);
            return (
              <button key={s} data-testid={`spec-${s}`} onClick={() => set({ ...f, specialties: on ? f.specialties.filter(x=>x!==s) : [...f.specialties, s] })} className={`chip capitalize ${on ? "border-gold bg-gold text-ink font-semibold" : ""}`}>{s}</button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">Bio</p>
          <button data-testid="voice-bio" onClick={() => toast.info("Voice-to-text ships post-prototype.")} className="chip text-[10px]"><Microphone size={10} /> Voice</button>
        </div>
        <textarea data-testid="in-bio" rows={4} className="w-full bg-transparent border border-cream/15 p-3 text-cream focus:border-gold outline-none" placeholder="I've been barbering for 12 years and specialise in skin fades, tapers and beard work. I'm building my own client base after hours and focusing on clean, consistent cuts." value={f.bio} onChange={(e) => set({ ...f, bio: e.target.value })} />
      </div>
      <Next onClick={() => onNext(f)} disabled={!ok} />
    </div>
  );
}

function StepVerify({ data, onNext }) {
  const [items, setItems] = useState(data.verification || { identity: "not_started", email: "verified", mobile: "not_started", wwc: "not_started", police: "not_started", licence: "not_started" });
  const simulate = (k) => setItems({ ...items, [k]: "verified" });
  const all = items.identity === "verified" && items.mobile === "verified";
  return (
    <div className="space-y-4" data-testid="ob-step-verify">
      <h1 className="font-display text-3xl">Verify yourself.</h1>
      <p className="text-xs text-cream/60">Simulated / DEMO verification. Real KYC ships post-prototype. Members never see raw ID.</p>
      {[
        ["identity","Identity"],
        ["email","Email"],
        ["mobile","Mobile"],
        ["wwc","Working With Children (optional)"],
        ["police","Police Check (optional)"],
        ["licence","Relevant licence (optional)"],
      ].map(([k, label]) => (
        <div key={k} className="border border-cream/10 p-4 flex items-center justify-between" data-testid={`verif-${k}`}>
          <div className="flex items-center gap-3">
            {items[k] === "verified" ? <ShieldCheck size={20} color="#C5A059" weight="fill" /> : <Warning size={20} color="#C5A059" />}
            <p className="text-sm">{label}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-cream/60">{items[k].replace("_"," ")}</span>
            {items[k] !== "verified" && <button data-testid={`verify-${k}`} onClick={() => simulate(k)} className="chip hover:border-gold text-[10px]">Simulate</button>}
          </div>
        </div>
      ))}
      <Next onClick={() => onNext({ verification: items })} disabled={!all} />
    </div>
  );
}

function StepStudio({ data, onNext }) {
  const [f, set] = useState({
    studio_name: data.studio_name || "",
    area: data.area || "",
    city: data.city || "Darwin",
    exact_address: data.exact_address || "",
    tagline: data.tagline || "",
    studio_notes: data.studio_notes || "",
    parking: data.parking || false,
    pets: data.pets || false,
    non_smoking: data.non_smoking ?? true,
    accessibility: data.accessibility || "",
    entry_instructions: data.entry_instructions || "",
    children_welcome: data.children_welcome || false,
  });
  const ok = f.studio_name && f.area && f.exact_address;
  return (
    <div className="space-y-5" data-testid="ob-step-studio">
      <h1 className="font-display text-3xl">Build your Studio.</h1>
      <Row label="Studio name"><input data-testid="in-studio-name" className="input-line" value={f.studio_name} onChange={(e) => set({...f, studio_name: e.target.value})} /></Row>
      <Row label="Tagline"><input data-testid="in-tagline" className="input-line" value={f.tagline} onChange={(e) => set({...f, tagline: e.target.value})} /></Row>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Approximate suburb"><input data-testid="in-area" className="input-line" value={f.area} onChange={(e) => set({...f, area: e.target.value})} /></Row>
        <Row label="City"><input data-testid="in-city" className="input-line" value={f.city} onChange={(e) => set({...f, city: e.target.value})} /></Row>
      </div>
      <Row label="Private exact address (never public until 90 min before)">
        <input data-testid="in-exact-address" className="input-line" value={f.exact_address} onChange={(e) => set({...f, exact_address: e.target.value})} />
      </Row>
      <Row label="Entry instructions"><input data-testid="in-entry" className="input-line" value={f.entry_instructions} onChange={(e) => set({...f, entry_instructions: e.target.value})} /></Row>
      <div className="grid grid-cols-2 gap-3">
        {[["parking","Parking available"],["pets","Pets on premises"],["non_smoking","Non-smoking"],["children_welcome","Children welcome"]].map(([k,l]) => (
          <label key={k} className="flex items-center gap-2 text-sm" data-testid={`toggle-${k}`}>
            <input type="checkbox" checked={f[k]} onChange={(e) => set({...f, [k]: e.target.checked})} className="accent-[#C5A059] w-5 h-5" /> {l}
          </label>
        ))}
      </div>
      <Row label="Accessibility notes"><input data-testid="in-accessibility" className="input-line" value={f.accessibility} onChange={(e) => set({...f, accessibility: e.target.value})} /></Row>
      <Row label="Studio notes for Members"><textarea data-testid="in-studio-notes" rows={2} className="w-full bg-transparent border border-cream/15 p-3 text-cream focus:border-gold outline-none" value={f.studio_notes} onChange={(e) => set({...f, studio_notes: e.target.value})} /></Row>
      <Next onClick={() => onNext(f)} disabled={!ok} />
    </div>
  );
}

function StepPortfolio({ data, onNext }) {
  const [items, setItems] = useState(data.portfolio || []);
  const [tags, setTags] = useState(data.portfolio_tags || []);
  const add = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map(f => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); }))).then((urls) => {
      setItems([...items, ...urls]); setTags([...tags, ...urls.map(() => "Skin Fade")]);
    });
  };
  const remove = (i) => { setItems(items.filter((_,idx) => idx!==i)); setTags(tags.filter((_,idx) => idx!==i)); };
  return (
    <div className="space-y-5" data-testid="ob-step-portfolio">
      <h1 className="font-display text-3xl">Show your work.</h1>
      <p className="text-xs text-cream/60">3–6 strong examples. Hair-first, please.</p>
      <input data-testid="in-portfolio" type="file" accept="image/*" multiple onChange={add} className="text-xs text-cream/60" />
      <div className="grid grid-cols-3 gap-2">
        {items.map((src, i) => (
          <div key={i} className="relative border border-cream/10">
            <img src={src} alt="" className="w-full h-32 object-cover" />
            <select data-testid={`tag-${i}`} value={tags[i] || "Skin Fade"} onChange={(e) => setTags(tags.map((t,idx) => idx===i ? e.target.value : t))} className="w-full bg-ink border-t border-cream/10 text-[10px] p-1 text-cream">
              {["Skin Fade","Taper Fade","Cut + Beard","Braids","Colour","Curls","Women's Cut","Beard/Shave"].map(o => <option key={o}>{o}</option>)}
            </select>
            <button data-testid={`remove-${i}`} onClick={() => remove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-ink/70 flex items-center justify-center"><Trash size={12} /></button>
          </div>
        ))}
      </div>
      <Next onClick={() => onNext({ portfolio: items, portfolio_tags: tags })} disabled={items.length < 1} />
    </div>
  );
}

function StepServices({ data, onNext }) {
  const [rows, setRows] = useState(data.services?.length ? data.services : [
    { name: "Skin Fade", price: 50, duration_minutes: 35, description: "Zero to one, blended clean.", category: "Fade", after_hours_eligible: true, gallery: [] },
    { name: "Taper Fade", price: 45, duration_minutes: 30, description: "Soft taper.", category: "Fade", after_hours_eligible: true, gallery: [] },
    { name: "Cut + Beard", price: 65, duration_minutes: 50, description: "Cut plus shaped beard.", category: "Cut", after_hours_eligible: false, gallery: [] },
  ]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx===i ? {...r, [k]: v} : r));
  const add = () => setRows([...rows, { name: "New service", price: 40, duration_minutes: 30, description: "", category: "Cut", after_hours_eligible: false, gallery: [] }]);
  return (
    <div className="space-y-5" data-testid="ob-step-services">
      <h1 className="font-display text-3xl">Services.</h1>
      <p className="text-xs text-cream/60">You control your prices. KUTZ never bids or sets them.</p>
      {rows.map((r, i) => (
        <div key={i} className="border border-cream/10 p-4 space-y-3" data-testid={`svc-${i}`}>
          <div className="grid grid-cols-3 gap-3">
            <input className="input-line col-span-2" placeholder="Service name" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`svc-name-${i}`} />
            <input className="input-line" placeholder="Category" value={r.category} onChange={(e) => upd(i, "category", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className="input-line" type="number" min="0" placeholder="Price $" value={r.price} onChange={(e) => upd(i, "price", Number(e.target.value))} data-testid={`svc-price-${i}`} />
            <input className="input-line" type="number" min="5" placeholder="Duration (min)" value={r.duration_minutes} onChange={(e) => upd(i, "duration_minutes", Number(e.target.value))} data-testid={`svc-duration-${i}`} />
          </div>
          <textarea rows={2} className="w-full bg-transparent border border-cream/15 p-2 text-cream text-sm focus:border-gold outline-none" placeholder="Description" value={r.description} onChange={(e) => upd(i, "description", e.target.value)} />
          <label className="flex items-center gap-2 text-xs text-cream/80"><input type="checkbox" checked={r.after_hours_eligible} onChange={(e) => upd(i, "after_hours_eligible", e.target.checked)} className="accent-[#C5A059] w-4 h-4" /> After-hours eligible</label>
        </div>
      ))}
      <button onClick={add} data-testid="svc-add" className="chip hover:border-gold"><Plus size={12} /> Add service</button>
      <Next onClick={() => onNext({ services: rows })} disabled={rows.length < 1} />
    </div>
  );
}

function StepAvailability({ data, onNext }) {
  const [days, setDays] = useState(data.availability || { Mon: [], Tue: [], Wed: [["18:00","21:00"]], Thu: [["18:00","21:00"]], Fri: [], Sat: [], Sun: [] });
  const [repeat, setRepeat] = useState(data.repeat_weekly ?? true);
  const [after, setAfter] = useState(data.after_hours_enabled ?? true);
  const [prem, setPrem] = useState(data.after_hours_premium_pct ?? 20);
  const toggleDay = (d) => setDays({ ...days, [d]: days[d]?.length ? [] : [["18:00","21:00"]] });
  const now = new Date();
  const seed = () => {
    const out = [];
    ["Wed","Thu"].forEach((d, i) => {
      const start = new Date(now); start.setDate(now.getDate() + ((3 + i - now.getDay() + 7) % 7)); start.setHours(19, 0, 0, 0);
      out.push(start.toISOString());
    });
    return out;
  };
  return (
    <div className="space-y-5" data-testid="ob-step-availability">
      <h1 className="font-display text-3xl">Availability.</h1>
      <div className="grid grid-cols-7 gap-2">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <button key={d} data-testid={`day-${d}`} onClick={() => toggleDay(d)} className={`chip justify-center ${days[d]?.length ? "border-gold bg-gold text-ink font-semibold" : ""}`}>{d}</button>
        ))}
      </div>
      <p className="text-xs text-cream/60">Default 6–9 PM windows on selected days. Multiple windows per day + overrides ship in the desktop calendar (Stage 5+).</p>
      <label className="flex items-center gap-2 text-sm" data-testid="repeat-weekly"><input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} className="accent-[#C5A059] w-5 h-5" /> Repeat weekly</label>
      <div className="border border-cream/10 p-4">
        <label className="flex items-center gap-2 text-sm" data-testid="after-hours"><input type="checkbox" checked={after} onChange={(e) => setAfter(e.target.checked)} className="accent-[#C5A059] w-5 h-5" /> After-Hours Requests</label>
        {after && (
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">After-hours premium %</p>
            <input type="number" min="0" max="100" value={prem} onChange={(e) => setPrem(Number(e.target.value))} className="input-line" data-testid="ah-premium" />
          </div>
        )}
      </div>
      <Next onClick={() => onNext({ availability: days, repeat_weekly: repeat, after_hours_enabled: after, after_hours_premium_pct: prem, availability_slots: seed() })} />
    </div>
  );
}

function StepPreferences({ data, onNext }) {
  const [mode, setMode] = useState(data.booking_mode || "instant");
  const [verif, setVerif] = useState(data.requires_verified || false);
  return (
    <div className="space-y-5" data-testid="ob-step-preferences">
      <h1 className="font-display text-3xl">Booking preferences.</h1>
      <div className="grid grid-cols-3 gap-2">
        {[["instant","Instant Book"],["request","Request to Book"],["both","Both"]].map(([v, l]) => (
          <button key={v} data-testid={`mode-${v}`} onClick={() => setMode(v)} className={`chip justify-center ${mode === v ? "border-gold bg-gold text-ink font-semibold" : ""}`}>{l}</button>
        ))}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60 mb-2">Minimum Member verification</p>
        <div className="flex gap-2">
          <button data-testid="verif-basic" onClick={() => setVerif(false)} className={`chip ${!verif ? "border-gold bg-gold text-ink font-semibold" : ""}`}>Basic</button>
          <button data-testid="verif-identity" onClick={() => setVerif(true)} className={`chip ${verif ? "border-gold bg-gold text-ink font-semibold" : ""}`}>Identity Verified</button>
        </div>
      </div>
      <Next onClick={() => onNext({ booking_mode: mode, requires_verified: verif })} />
    </div>
  );
}

function StepPayout({ data, onNext }) {
  const [connected, setConnected] = useState(data.payout_connected || false);
  return (
    <div className="space-y-5" data-testid="ob-step-payout">
      <h1 className="font-display text-3xl">Get paid.</h1>
      <div className="border border-cream/10 p-5">
        <p className="text-sm text-cream/85">Connect a payout account so KUTZ can transfer your earnings.</p>
        {!connected ? (
          <button data-testid="connect-payout" onClick={() => setConnected(true)} className="btn-gold px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold mt-4">Connect payout account (demo)</button>
        ) : (
          <p className="text-xs text-gold mt-3 flex items-center gap-2"><CheckCircle size={14} weight="fill" /> Connected · Demo</p>
        )}
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-4">Real Stripe/payout wires up after Stage 8.</p>
      </div>
      <div className="border border-gold/40 bg-gold/5 p-5">
        <p className="overline mb-2">KUTZ Professional platform fee</p>
        <p className="font-display text-3xl text-gold">8%</p>
        <div className="mt-3 text-sm text-cream/85 space-y-1">
          <div className="flex justify-between"><span>Service price</span><span>$50.00</span></div>
          <div className="flex justify-between"><span>KUTZ platform fee (8%)</span><span>-$4.00</span></div>
          <div className="flex justify-between font-display text-lg"><span>Professional earnings</span><span>$46.00</span></div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/50 pt-2">Before applicable processing / tax items — connected in a later stage.</p>
        </div>
      </div>
      <Next onClick={() => onNext({ payout_connected: connected })} />
    </div>
  );
}

function StepPreview({ data, onNext }) {
  return (
    <div className="space-y-5" data-testid="ob-step-preview">
      <h1 className="font-display text-3xl">Preview my Studio.</h1>
      <p className="text-xs text-cream/60">This is exactly what Members will see. We reuse the production Studio component after launch.</p>
      <div className="border border-cream/10 p-5 space-y-3">
        <p className="overline">{data.tagline}</p>
        <h2 className="font-display text-3xl">{data.studio_name}</h2>
        <p className="text-xs text-cream/60">By {data.public_name} · Approximately {data.area}, {data.city}</p>
        <div className="grid grid-cols-3 gap-2">
          {(data.portfolio || []).slice(0, 6).map((src, i) => <img key={i} src={src} className="w-full aspect-square object-cover" alt=""/>)}
        </div>
        <div className="mt-3 space-y-2">
          {(data.services || []).map((s, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span>{s.name} · {s.duration_minutes} min</span>
              <span className="text-gold">${s.price}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-cream/60 mt-3">Exact address released 90 minutes before appointment.</p>
      </div>
      <Next onClick={() => onNext({})} label="Ready to launch" />
    </div>
  );
}

function StepLaunch({ data, onLaunched }) {
  const [launched, setLaunched] = useState(false);
  const [missing, setMissing] = useState([]);
  const [busy, setBusy] = useState(false);
  const launch = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/my/studio/launch", {});
      setLaunched(true);
      toast.success("Studio is live");
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || "Launch failed";
      setMissing([detail]);
    } finally { setBusy(false); }
  };

  if (launched) {
    return (
      <div className="text-center py-8" data-testid="launch-success">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold text-ink mb-6"><CheckCircle size={36} weight="fill" /></div>
        <p className="overline">Live</p>
        <h1 className="font-display text-5xl tracking-tighter">{(data.studio_name || "Your Studio")} IS LIVE.</h1>
        <p className="text-cream/70 mt-4">Your next client could be closer than you think.</p>
        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          <button onClick={onLaunched} data-testid="open-dashboard" className="btn-gold px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Open my dashboard</button>
          <button data-testid="view-studio" onClick={() => window.location.href="/my-studio"} className="btn-ghost px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">View my Studio</button>
          <button data-testid="share-studio" onClick={() => { navigator.clipboard?.writeText(window.location.origin); toast.success("Share link copied — QR ships post-prototype."); }} className="btn-ghost px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">Share my Studio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ob-step-launch">
      <h1 className="font-display text-4xl">Launch my Studio.</h1>
      {missing.length > 0 && (
        <div className="border border-red-400/40 bg-red-400/5 p-4 text-sm text-red-200" data-testid="launch-missing">
          {missing.map((m, i) => <p key={i}>{m}</p>)}
        </div>
      )}
      <button data-testid="launch-cta" onClick={launch} disabled={busy} className="btn-gold w-full px-6 py-4 rounded-full text-xs uppercase tracking-[0.3em] font-semibold disabled:opacity-40">
        {busy ? "Launching…" : "LAUNCH MY STUDIO"}
      </button>
    </div>
  );
}
